import React, { useState, useEffect } from "react";
import { useFormik } from "formik";
import Swal from "sweetalert2";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import * as Yup from "yup";
import Breadcrums from "../../common/breadcrumbs";
import {
    globalLoader,
    handleServerValidations,
} from "../../../../utils/commonfunction";
import { SWAL_SETTINGS } from "../../../../utils/Constants";
import { Update, Details } from "../../services/productAttributeValue.services";

const EditAttributeValue = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const { t } = useTranslation();
    const [submitted, setSubmitted] = useState(false);
    const [initialData, setInitialData] = useState({
        attribute_title: "",
        value: "",
        view_type: "row",
        image: null,
        file: null,
        preview: null,
        status: 1,
    });

    const breadcrumbs = [
        { title: t("link_dashboard"), url: "/admin/dashboard" },
        { title: "Attribute Values", url: "/admin/product-attribute-value/list/1" },
        { title: t("btn_edit"), url: "" },
    ];

    const getImageUrl = (imagePath) => {
        if (!imagePath) return null;
        if (imagePath.startsWith("http://") || imagePath.startsWith("https://")) return imagePath;
        if (imagePath.startsWith("public/")) {
            return `${process.env.REACT_APP_API_URL || ""}/${imagePath}`;
        }
        return `${process.env.REACT_APP_API_URL || ""}/public/uploads/attribute_values/${imagePath}`;
    };

    useEffect(() => {
        if (id) {
            globalLoader(true);
            Details(id)
                .then((response) => {
                    if (response && response.success) {
                        const data = response.data;
                        setInitialData({
                            attribute_title: data.attribute?.title || "",
                            value: data.value || "",
                            view_type: data.view_type || "row",
                            image: data.image || null,
                            file: null,
                            preview: data.image ? getImageUrl(data.image) : null,
                            status: data.status,
                        });
                    }
                    globalLoader(false);
                })
                .catch((error) => {
                    console.log("error fetching details", error);
                    globalLoader(false);
                });
        }
    }, [id]);

    const validationSchema = Yup.object().shape({
        value: Yup.string().trim().required("Value is required")
            .max(255, "Value cannot exceed 255 characters"),
        view_type: Yup.string().required("View type is required"),
    });

    const formik = useFormik({
        enableReinitialize: true,
        initialValues: initialData,
        validationSchema: validationSchema,
        onSubmit: (values) => {
            globalLoader(true);
            setSubmitted(true);

            const formData = new FormData();
            formData.append("id", id);
            formData.append("value", values.value);
            formData.append("view_type", values.view_type || "row");
            formData.append("status", values.status);
            if (values.file) {
                formData.append("image", values.file);
            }

            Update(formData)
                .then((response) => {
                    if (response && response.success) {
                        Swal.fire({
                            icon: "success",
                            text: response.message || "Success",
                            ...SWAL_SETTINGS,
                        });
                        globalLoader(false);
                        setTimeout(() => {
                            navigate(`/admin/product-attribute-value/list/1`);
                        }, 1000);
                    } else {
                        Swal.fire({
                            icon: "error",
                            text: response ? handleServerValidations(response) : "Something went wrong",
                            ...SWAL_SETTINGS,
                        });
                        globalLoader(false);
                        setSubmitted(false);
                    }
                })
                .catch((error) => {
                    console.log("error ====> ", error);
                    globalLoader(false);
                    setSubmitted(false);
                });
        },
    });

    return (
        <>
            <Breadcrums data={breadcrumbs} />
            <div className="animation_fade">
                <div className="card custom-card">
                    <div className="card-body">
                        <h6 className="main-content-label mb-3">Edit Attribute Value</h6>
                        <form onSubmit={formik.handleSubmit}>
                            <div className="row row-sm">
                                {/* Attribute (Read-only) */}
                                <div className="col-lg-6 form-group">
                                    <label className="text-left d-flex">Attribute :</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        value={formik.values.attribute_title}
                                        disabled
                                    />
                                </div>

                                {/* Value */}
                                <div className="col-lg-6 form-group">
                                    <label htmlFor="value" className="text-left d-flex">
                                        Value :<span className="requirestar">*</span>
                                    </label>
                                    <input
                                        name="value"
                                        id="value"
                                        type="text"
                                        className="form-control"
                                        placeholder="Enter value"
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        value={formik.values.value}
                                    />
                                    {formik.errors.value && formik.touched.value && (
                                        <span className="text-danger d-flex text-left">
                                            {formik.errors.value}
                                        </span>
                                    )}
                                </div>

                                {/* View Type */}
                                <div className="col-lg-6 form-group">
                                    <label htmlFor="view_type" className="text-left d-flex">
                                        View Type :<span className="requirestar">*</span>
                                    </label>
                                    <select
                                        name="view_type"
                                        id="view_type"
                                        className="form-control"
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        value={formik.values.view_type}
                                    >
                                        <option value="row">Row</option>
                                        <option value="column">Column</option>
                                    </select>
                                    {formik.errors.view_type && formik.touched.view_type && (
                                        <span className="text-danger d-flex text-left">
                                            {formik.errors.view_type}
                                        </span>
                                    )}
                                </div>

                                {/* Image Upload */}
                                <div className="col-lg-6 form-group">
                                    <label htmlFor="image" className="text-left d-flex">
                                        Image (Optional) :
                                    </label>
                                    <div className="d-flex align-items-center gap-3">
                                        <input
                                            type="file"
                                            name="image"
                                            id="image"
                                            accept="image/*"
                                            className="form-control"
                                            onChange={(e) => {
                                                if (e.target.files && e.target.files[0]) {
                                                    const file = e.target.files[0];
                                                    formik.setFieldValue("file", file);
                                                    const reader = new FileReader();
                                                    reader.onload = (ev) => {
                                                        formik.setFieldValue("preview", ev.target.result);
                                                    };
                                                    reader.readAsDataURL(file);
                                                }
                                            }}
                                        />
                                        {formik.values.preview && (
                                            <img
                                                src={formik.values.preview}
                                                alt="preview"
                                                style={{ width: "40px", height: "40px", objectFit: "cover", borderRadius: "4px", border: "1px solid #ccc" }}
                                            />
                                        )}
                                    </div>
                                </div>

                                {/* Status */}
                                <div className="col-lg-6 form-group">
                                    <label htmlFor="status" className="text-left d-flex">
                                        Status :
                                    </label>
                                    <select
                                        name="status"
                                        id="status"
                                        className="form-control"
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        value={formik.values.status}
                                    >
                                        <option value={1}>{t("Active")}</option>
                                        <option value={0}>{t("Inactive")}</option>
                                    </select>
                                </div>

                                <div className="col-md-12 mt-3">
                                    <button
                                        className="btn btn-main-primary signbtn mr-2"
                                        type="submit"
                                        disabled={submitted}
                                    >
                                        <i className="ace-icon fa fa-check bigger-110 mx-1"></i>
                                        {t("btn_update")}
                                    </button>
                                    <button
                                        className="btn ripple btn-secondary"
                                        type="button"
                                        onClick={() => navigate(-1)}
                                    >
                                        <i className="ace-icon fa fa-times bigger-110 mx-1"></i>
                                        {t("btn_cancel")}
                                    </button>
                                </div>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </>
    );
};

export default EditAttributeValue;
